// src/utils/calcoliStrumenti.js
// ═══════════════════════════════════════════════════════════════════════════
// FORMULE DEGLI STRUMENTI TECNICI (W442) — logica pura, provata a parte (prove/strumenti.mjs)
// ═══════════════════════════════════════════════════════════════════════════
// Ogni funzione riceve numeri già letti (utils/numeriStrumenti.js) e restituisce null quando i dati
// non bastano o non hanno senso (negativi, zero dove serve un divisore): lo strumento mostra «—».
// Le unità sono scritte nel nome o nel commento di ogni funzione.
// ═══════════════════════════════════════════════════════════════════════════

const ok = (...n) => n.every(x => Number.isFinite(x));
const pos = (...n) => n.every(x => Number.isFinite(x) && x > 0);
const nonNeg = (...n) => n.every(x => Number.isFinite(x) && x >= 0);
export const arrotonda = (n, dec = 2) => Math.round(n * 10 ** dec) / 10 ** dec;

// ── Correzioni agli strumenti esistenti ─────────────────────────────────────

/**
 * Scadenza di pagamento. dffm = «data fattura fine mese».
 * Prassi: con giorni multipli di 30 si contano i mesi interi dalla fine del mese della fattura
 * (26/05, 60 gg DFFM → 31/07; 31/01, 30 gg DFFM → 28/02); con altri giorni si aggiungono i giorni
 * alla data della fattura e si va a fine di quel mese. Senza DFFM: data fattura + giorni.
 */
export function scadenzaPagamento(dataFattura, giorni, dffm) {
  if (!dataFattura || !nonNeg(giorni)) return null;
  const d = new Date(dataFattura + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  if (!dffm) { const s = new Date(d); s.setDate(s.getDate() + giorni); return s; }
  if (giorni % 30 === 0) return new Date(d.getFullYear(), d.getMonth() + giorni / 30 + 1, 0);
  const s = new Date(d); s.setDate(s.getDate() + giorni);
  return new Date(s.getFullYear(), s.getMonth() + 1, 0);
}

/** Anni di ammortamento: con il primo anno a metà aliquota servono 1 + ceil((100 − al/2)/al) anni. */
export function durataAmmortamento(aliquotaPct, primoAnnoRidotto) {
  if (!pos(aliquotaPct) || aliquotaPct > 100) return null;
  if (!primoAnnoRidotto) return Math.ceil(100 / aliquotaPct - 1e-9);
  return 1 + Math.ceil((100 - aliquotaPct / 2) / aliquotaPct - 1e-9);
}

/** Resistività del conduttore alla temperatura T (°C): ρ(T) = ρ20·(1 + α·(T − 20)). Ω·mm²/m */
export const RHO_20 = { rame: 0.0178, alluminio: 0.0286 };
export const ALFA = { rame: 0.00393, alluminio: 0.00403 };
export function resistivitaA(materiale, temperatura = 20) {
  const r = RHO_20[materiale], a = ALFA[materiale];
  if (!r || !ok(temperatura)) return null;
  return r * (1 + a * (temperatura - 20));
}

/**
 * Protezione delle stringhe FV, CEI 64-8 sez. 712: Isc max = 1,25·Isc STC; la protezione
 * serve se (Np − 1)·Isc max > 1,35·IMOD_MAX_OCPR; se serve, In va scelta tra 1,1·Isc max e IMOD_MAX_OCPR.
 */
export function protezioneStringheFV(iscStc, nStringhe, imodMaxOcpr) {
  if (!pos(iscStc) || !(nStringhe >= 1)) return null;
  const iscMax = 1.25 * iscStc;
  const inMin = 1.1 * iscMax;
  if (!pos(imodMaxOcpr)) return { iscMax, inMin, inMax: null, serve: null, correnteInversa: (nStringhe - 1) * iscMax };
  const correnteInversa = (nStringhe - 1) * iscMax;
  const serve = correnteInversa > 1.35 * imodMaxOcpr;
  return { iscMax, inMin, inMax: imodMaxOcpr, serve, correnteInversa, possibile: inMin <= imodMaxOcpr };
}

// ── Calcoli matematici ──────────────────────────────────────────────────────

/** Proporzione a : b = c : x  →  x = b·c / a */
export function proporzione(a, b, c) { return ok(a, b, c) && a !== 0 ? (b * c) / a : null; }

/** Media semplice (pesi assenti) o ponderata Σ(v·p)/Σp. righe: [{ valore, peso }] */
export function media(righe) {
  const v = (righe || []).filter(r => ok(r.valore));
  if (!v.length) return null;
  const ponderata = v.some(r => r.peso !== undefined && r.peso !== null && !Number.isNaN(r.peso));
  if (!ponderata) return { media: v.reduce((t, r) => t + r.valore, 0) / v.length, n: v.length, ponderata: false };
  if (v.some(r => !nonNeg(r.peso))) return null;
  const sp = v.reduce((t, r) => t + r.peso, 0);
  if (sp <= 0) return null;
  return { media: v.reduce((t, r) => t + r.valore * r.peso, 0) / sp, n: v.length, ponderata: true, sommaPesi: sp };
}

/** «7:30», «7.30» (con «:»), «7,5» (ore decimali) → minuti. null se non leggibile. */
export function minutiDaTesto(testo) {
  const t = String(testo ?? "").trim();
  if (!t) return null;
  const neg = t.startsWith("-");
  const s = neg ? t.slice(1).trim() : t;
  let m = null;
  const hm = s.match(/^(\d+):(\d{1,2})$/);
  if (hm) { if (+hm[2] >= 60) return null; m = (+hm[1]) * 60 + (+hm[2]); }
  else if (/^\d+([.,]\d+)?$/.test(s)) m = Math.round(parseFloat(s.replace(",", ".")) * 60);
  if (m === null) return null;
  return neg ? -m : m;
}
/** minuti → «h:mm» (anche oltre le 24 ore) */
export function testoDaMinuti(minuti) {
  if (!ok(minuti)) return "—";
  const neg = minuti < 0; const a = Math.abs(Math.round(minuti));
  return `${neg ? "−" : ""}${Math.floor(a / 60)}:${String(a % 60).padStart(2, "0")}`;
}

/** Conversione di unità: fattori verso l'unità base di ogni grandezza (m, m², m³, kg). */
export const UNITA = {
  lunghezza: { base: "m", unita: { mm: 0.001, cm: 0.01, dm: 0.1, m: 1, km: 1000, "pollice (in)": 0.0254, "piede (ft)": 0.3048, "iarda (yd)": 0.9144, "miglio (mi)": 1609.344 } },
  superficie: { base: "m²", unita: { "mm²": 1e-6, "cm²": 1e-4, "m²": 1, "ara (a)": 100, "ettaro (ha)": 10000, "km²": 1e6, "piede² (ft²)": 0.09290304 } },
  volume: { base: "m³", unita: { "ml (cm³)": 1e-6, "litro (dm³)": 0.001, "m³": 1, "gallone USA": 0.003785411784, "piede³ (ft³)": 0.028316846592 } },
  massa: { base: "kg", unita: { g: 0.001, kg: 1, "quintale (q)": 100, "tonnellata (t)": 1000, "libbra (lb)": 0.45359237, "oncia (oz)": 0.028349523125 } },
};
export function convertiUnita(valore, grandezza, da, a) {
  const g = UNITA[grandezza];
  if (!g || !ok(valore) || !g.unita[da] || !g.unita[a]) return null;
  return (valore * g.unita[da]) / g.unita[a];
}

// ── Amministrativo ──────────────────────────────────────────────────────────

/** «50+10+5» o «50 10 5» → [50, 10, 5]. Accetta la virgola decimale («12,5+3»). */
export function scontiDaTesto(testo) {
  const parti = String(testo ?? "").split(/[+;\s/]+/).map(x => x.trim()).filter(Boolean);
  const v = parti.map(x => parseFloat(x.replace("%", "").replace(",", ".")));
  return v.every(x => Number.isFinite(x)) ? v : null;
}
/** Sconti a cascata: netto = prezzo·Π(1 − s_i/100); sconto equivalente = 1 − netto/prezzo. */
export function scontiACascata(prezzo, sconti) {
  if (!nonNeg(prezzo) || !Array.isArray(sconti) || !sconti.length) return null;
  if (sconti.some(s => !Number.isFinite(s) || s < 0 || s > 100)) return null;
  let netto = prezzo; const passi = [];
  for (const s of sconti) { const prima = netto; netto = netto * (1 - s / 100); passi.push({ sconto: s, prima, dopo: netto }); }
  return { netto, risparmio: prezzo - netto, scontoEquivalente: prezzo > 0 ? (1 - netto / prezzo) * 100 : (1 - sconti.reduce((p, s) => p * (1 - s / 100), 1)) * 100, passi };
}

/** Ripartizione di un importo in quote (pesi qualsiasi: %, millesimi, m²…), al centesimo:
 *  ogni quota è arrotondata al centesimo e la differenza di arrotondamento va sull'ultima riga. */
export function ripartisci(importo, quote) {
  if (!nonNeg(importo) || !Array.isArray(quote) || !quote.length || quote.some(q => !nonNeg(q))) return null;
  const tot = quote.reduce((t, q) => t + q, 0);
  if (tot <= 0) return null;
  const cent = Math.round(importo * 100);
  const parti = quote.map(q => Math.round((cent * q) / tot));
  const diff = cent - parti.reduce((t, p) => t + p, 0);
  parti[parti.length - 1] += diff;
  return { totaleQuote: tot, righe: quote.map((q, i) => ({ quota: q, percentuale: (q / tot) * 100, importo: parti[i] / 100 })), arrotondamento: diff / 100 };
}

// ── Finanza aziendale ───────────────────────────────────────────────────────

/** Margine e ricarico da costo e prezzo di vendita. margine% = (P − C)/P · 100 ; ricarico% = (P − C)/C · 100 */
export function margineRicarico(costo, prezzo) {
  if (!pos(costo) || !pos(prezzo)) return null;
  const utile = prezzo - costo;
  return { utile, marginePct: (utile / prezzo) * 100, ricaricoPct: (utile / costo) * 100 };
}
/** Prezzo dal costo con un ricarico %: P = C·(1 + r) */
export const prezzoDaRicarico = (costo, ricaricoPct) => (nonNeg(costo) && ok(ricaricoPct) && ricaricoPct > -100 ? costo * (1 + ricaricoPct / 100) : null);
/** Prezzo dal costo con un margine %: P = C/(1 − m) — il margine deve essere sotto il 100% */
export const prezzoDaMargine = (costo, marginePct) => (nonNeg(costo) && ok(marginePct) && marginePct < 100 ? costo / (1 - marginePct / 100) : null);
/** m = r/(1 + r) ; r = m/(1 − m) (in %) */
export const margineDaRicarico = (r) => (ok(r) && r > -100 ? (r / (100 + r)) * 100 : null);
export const ricaricoDaMargine = (m) => (ok(m) && m < 100 ? (m / (100 - m)) * 100 : null);

/** Preventivo vs consuntivo: scostamento = consuntivo − preventivo (positivo = si è speso di più). */
export function scostamento(preventivo, consuntivo) {
  if (!nonNeg(preventivo) || !nonNeg(consuntivo)) return null;
  const diff = consuntivo - preventivo;
  return { diff, pct: preventivo > 0 ? (diff / preventivo) * 100 : null };
}

/** Rientro di un investimento: anni = investimento / beneficio netto annuo;
 *  ROI sul periodo = (beneficio·anni − investimento)/investimento. Senza interessi né inflazione. */
export function rientroInvestimento(investimento, beneficioAnnuo, anniValutazione) {
  if (!pos(investimento) || !ok(beneficioAnnuo)) return null;
  const payback = beneficioAnnuo > 0 ? investimento / beneficioAnnuo : null;
  const n = pos(anniValutazione) ? anniValutazione : null;
  const guadagno = n !== null ? beneficioAnnuo * n - investimento : null;
  return { paybackAnni: payback, paybackMesi: payback !== null ? payback * 12 : null, guadagnoNetto: guadagno, roiPct: guadagno !== null ? (guadagno / investimento) * 100 : null };
}

// ── Elettrico ───────────────────────────────────────────────────────────────

/** Consumo e costo: kWh = W/1000 · ore/giorno · giorni ; € = kWh · €/kWh */
export function consumoEnergia(potenzaW, oreGiorno, giorni, prezzoKwh) {
  if (!nonNeg(potenzaW, oreGiorno, giorni) || oreGiorno > 24) return null;
  const kwh = (potenzaW / 1000) * oreGiorno * giorni;
  return { kwh, costo: nonNeg(prezzoKwh) ? kwh * prezzoKwh : null, kwhGiorno: (potenzaW / 1000) * oreGiorno };
}

// ── Fotovoltaico ────────────────────────────────────────────────────────────

/** Campo FV: n moduli = ceil(kWp obiettivo·1000 / Wp modulo); rapporto DC/AC = kWp / kW inverter. */
export function campoFotovoltaico(kwpObiettivo, wpModulo, kwInverter, mqModulo) {
  if (!pos(kwpObiettivo, wpModulo)) return null;
  const n = Math.ceil((kwpObiettivo * 1000) / wpModulo - 1e-9);
  const kwp = (n * wpModulo) / 1000;
  return { moduli: n, kwp, rapportoDcAc: pos(kwInverter) ? kwp / kwInverter : null, superficie: pos(mqModulo) ? n * mqModulo : null };
}

/** Produzione e risparmio indicativi: kWh = kWp · resa specifica (kWh/kWp·anno, dato dell'utente);
 *  autoconsumati = kWh·%; risparmio = autoconsumati·prezzo acquisto; ceduti·prezzo cessione. */
export function produzioneFotovoltaico(kwp, resaSpecifica, autoconsumoPct, prezzoAcquisto, prezzoCessione) {
  if (!pos(kwp, resaSpecifica) || !nonNeg(autoconsumoPct) || autoconsumoPct > 100) return null;
  const kwh = kwp * resaSpecifica;
  const auto = (kwh * autoconsumoPct) / 100, ceduti = kwh - auto;
  const risparmio = nonNeg(prezzoAcquisto) ? auto * prezzoAcquisto : null;
  const ricavo = nonNeg(prezzoCessione) ? ceduti * prezzoCessione : null;
  return { kwh, autoconsumati: auto, ceduti, risparmio, ricavoCessione: ricavo, beneficio: (risparmio ?? 0) + (ricavo ?? 0) };
}

// ── Illuminazione ───────────────────────────────────────────────────────────

/** Metodo del flusso totale: N = E·A / (Φ·CU·MF) arrotondato per eccesso; E medio ottenuto con N apparecchi. */
export function numeroApparecchi(luxRichiesti, areaMq, lumenApparecchio, cu, mf) {
  if (!pos(luxRichiesti, areaMq, lumenApparecchio, cu, mf) || cu > 1 || mf > 1) return null;
  const esatto = (luxRichiesti * areaMq) / (lumenApparecchio * cu * mf);
  const n = Math.ceil(esatto - 1e-9);
  return { esatto, n, luxOttenuti: (n * lumenApparecchio * cu * mf) / areaMq };
}

/** Sostituzione con LED: kWh risparmiati = n·(W vecchio − W nuovo)/1000·ore/anno; rientro = costo / risparmio €. */
export function sostituzioneLed(n, wVecchio, wNuovo, oreAnno, prezzoKwh, costoUnitario) {
  if (!pos(n, wVecchio) || !nonNeg(wNuovo, oreAnno) || wNuovo >= wVecchio) return null;
  const kwh = (n * (wVecchio - wNuovo) * oreAnno) / 1000;
  const euro = nonNeg(prezzoKwh) ? kwh * prezzoKwh : null;
  const spesa = nonNeg(costoUnitario) ? n * costoUnitario : null;
  return { kwhAnno: kwh, euroAnno: euro, spesa, rientroAnni: euro && spesa !== null ? spesa / euro : null, riduzionePct: ((wVecchio - wNuovo) / wVecchio) * 100 };
}

// ── Antintrusione ───────────────────────────────────────────────────────────

/** Capacità batterie: Ah = (I riposo·h riposo + I allarme·h allarme) / fattore (efficienza/invecchiamento).
 *  Correnti in A, tempi in ore. Taglia commerciale = la prima ≥ Ah. */
export const TAGLIE_BATTERIE_AH = [1.2, 2.2, 7.2, 12, 17, 18, 24, 26, 38, 40, 65, 100];
export function capacitaBatteria(iRiposoA, oreRiposo, iAllarmeA, oreAllarme, fattore = 0.8) {
  if (!nonNeg(iRiposoA, oreRiposo, iAllarmeA, oreAllarme) || !pos(fattore) || fattore > 1) return null;
  const ah = (iRiposoA * oreRiposo + iAllarmeA * oreAllarme) / fattore;
  if (ah <= 0) return null;
  return { ah, taglia: TAGLIE_BATTERIE_AH.find(t => t >= ah - 1e-9) || null };
}

// ── TVCC ────────────────────────────────────────────────────────────────────

/** Giorni di registrazione: giorni = TB·10¹²·8 / (n·Mbit/s·10⁶·3600·ore al giorno). TB decimali (come venduti). */
export function giorniRegistrazione(tbDisco, nTelecamere, mbps, oreGiorno = 24) {
  if (!pos(tbDisco, nTelecamere, mbps, oreGiorno) || oreGiorno > 24) return null;
  const byteGiorno = (nTelecamere * mbps * 1e6 * 3600 * oreGiorno) / 8;
  return { giorni: (tbDisco * 1e12) / byteGiorno, gbGiorno: byteGiorno / 1e9 };
}

// ── Condizionamento ─────────────────────────────────────────────────────────

/** Consumo elettrico: kW el = kW resa / EER (o COP); kWh = kW el · ore · giorni · carico%. */
export function consumoClima(kwResa, eer, oreGiorno, giorni, caricoPct, prezzoKwh) {
  if (!pos(kwResa, eer) || !nonNeg(oreGiorno, giorni) || oreGiorno > 24) return null;
  const c = pos(caricoPct) ? Math.min(caricoPct, 100) / 100 : 1;
  const kwEl = kwResa / eer;
  const kwh = kwEl * oreGiorno * giorni * c;
  return { kwElettrici: kwEl, kwh, costo: nonNeg(prezzoKwh) ? kwh * prezzoKwh : null };
}

/** Portata d'aria da ricambi orari: Q (m³/h) = V·n ; V = L·P·H. Anche in l/s (÷3,6). */
export function portataAria(volumeMq3, ricambiOra) {
  if (!pos(volumeMq3) || !nonNeg(ricambiOra)) return null;
  const q = volumeMq3 * ricambiOra;
  return { m3h: q, ls: q / 3.6 };
}

// ── Idraulico ───────────────────────────────────────────────────────────────

/** Acqua contenuta in una tubazione: litri = π·d²/4·L (d interno in mm, L in m). */
export function volumeTubazione(dInternoMm, lunghezzaM) {
  if (!pos(dInternoMm) || !nonNeg(lunghezzaM)) return null;
  const litriAlMetro = (Math.PI * (dInternoMm / 1000) ** 2 / 4) * 1000;
  return { litri: litriAlMetro * lunghezzaM, litriAlMetro };
}

/** Energia per scaldare l'acqua: kWh = litri·1,163·ΔT/1000 / rendimento (1,163 Wh per litro e °C). */
export function energiaAcqua(litri, tIniziale, tFinale, rendimentoPct = 100, prezzoKwh) {
  if (!pos(litri) || !ok(tIniziale, tFinale) || tFinale <= tIniziale || !pos(rendimentoPct) || rendimentoPct > 100) return null;
  const utile = (litri * 1.163 * (tFinale - tIniziale)) / 1000;
  const kwh = utile / (rendimentoPct / 100);
  return { kwhUtili: utile, kwh, costo: nonNeg(prezzoKwh) ? kwh * prezzoKwh : null };
}

// ── Reti dati ───────────────────────────────────────────────────────────────

/** Bobine di cavo: metri = punti·lunghezza media·(1 + scorta%); bobine = ceil(metri/metri per bobina).
 *  Una tratta non si giunta: si controlla anche quante tratte intere escono da una bobina. */
export function bobineCavo(punti, lunghezzaMedia, scortaPct = 10, metriBobina = 305) {
  if (!pos(punti, lunghezzaMedia, metriBobina) || !nonNeg(scortaPct)) return null;
  const tratta = lunghezzaMedia * (1 + scortaPct / 100);
  const metri = punti * tratta;
  const trattePerBobina = Math.floor(metriBobina / tratta + 1e-9);
  const bobineMetri = Math.ceil(metri / metriBobina - 1e-9);
  const bobineTratte = trattePerBobina > 0 ? Math.ceil(punti / trattePerBobina) : null;
  return { metri, tratta, trattePerBobina, bobine: bobineTratte !== null ? Math.max(bobineMetri, bobineTratte) : null, bobineSoloMetri: bobineMetri };
}

/** Unità rack: somma delle U degli apparati + scorta%; armadio = prima taglia commerciale ≥ totale. */
export const TAGLIE_RACK_U = [6, 9, 12, 15, 18, 22, 24, 27, 32, 36, 42, 47];
export function unitaRack(unitaApparati, scortaPct = 30) {
  if (!Array.isArray(unitaApparati) || !unitaApparati.length || unitaApparati.some(u => !pos(u)) || !nonNeg(scortaPct)) return null;
  const occupate = unitaApparati.reduce((t, u) => t + u, 0);
  const necessarie = Math.ceil(occupate * (1 + scortaPct / 100) - 1e-9);
  return { occupate, necessarie, armadio: TAGLIE_RACK_U.find(t => t >= necessarie) || null };
}

// ── Geometra / Edile ────────────────────────────────────────────────────────

/** Pittura di una stanza: pareti = 2·(L + P)·H − aperture; + soffitto L·P se scelto;
 *  litri = superficie · mani / resa (m²/l per mano); confezioni = ceil(litri / litri confezione). */
export function pitturaStanza({ lunghezza, larghezza, altezza, aperture = 0, soffitto = false, mani = 2, resa, confezione }) {
  if (!pos(lunghezza, larghezza, altezza, resa) || !nonNeg(aperture) || !(mani >= 1)) return null;
  const pareti = 2 * (lunghezza + larghezza) * altezza - aperture;
  if (pareti < 0) return null;
  const sup = pareti + (soffitto ? lunghezza * larghezza : 0);
  const litri = (sup * mani) / resa;
  return { pareti, superficie: sup, litri, confezioni: pos(confezione) ? Math.ceil(litri / confezione - 1e-9) : null };
}

/** Intonaco, rasante o massetto a sacchi: kg = m² · spessore mm · consumo (kg/m² per mm) · (1 + scarto%);
 *  sacchi = ceil(kg / kg per sacco). */
export function materialeASacchi(superficie, spessoreMm, consumoKgMqMm, kgSacco, scartoPct = 0) {
  if (!pos(superficie, spessoreMm, consumoKgMqMm) || !nonNeg(scartoPct)) return null;
  const kg = superficie * spessoreMm * consumoKgMqMm * (1 + scartoPct / 100);
  return { kg, sacchi: pos(kgSacco) ? Math.ceil(kg / kgSacco - 1e-9) : null };
}
